/**
 * SDK 独立请求层(B3)
 *
 * 基于 plain fetch(不用 axios/react-query,省 bundle)。
 * - 请求基址：同源 basePath（/hub/api/*）--业务请求经 hotel-hub BFF 服务端推导 hotelId/tenant/brand
 *   + 持密钥转发 whapi，不再跨源直连 whapi（消除 SDK 集成方域名 CORS 白名单）。
 * - 第 4 步起：hotelId / X-Tenant-Id / X-Brand-Id 不再由 SDK 注入--BFF callUpstream 服务端按域名
 *   推导（覆盖客户端，不信任）。SDK 组件（date-bar/shared.ts）仍调 resolveHotel 做酒店信息展示 +
 *   域名校验（缓存由组件侧填充）。请求仅带 Authorization（BFF whapi 端点透传 token）。
 * - 401 自处理:reject SdkError(401) + 触发 onUnauthorized 回调(阶段3 接登录弹窗)
 *
 * hotel/info 自身不经过这里(见 ./hotel.ts,避免循环依赖)。
 */
import { getConfig } from '../config';
import { getDomain } from './hotel';
import { SdkError } from '../errors';
import { buildContextHeaders } from '@/lib/api/inject';

const DEFAULT_TIMEOUT = 15000;

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  /** query 参数(任意方法均拼入 URL) */
  params?: Record<string, unknown>;
  /** 请求体(POST/PUT/PATCH) */
  body?: unknown;
  /** 自定义超时(毫秒) */
  timeout?: number;
}

/** 401 处理器集合(阶段3 由 booking 流程接 hotel-hub 登录弹窗) */
const unauthorizedHandlers = new Set<() => void>();

/** 注册 401 处理器,返回取消注册函数 */
export function onUnauthorized(handler: () => void): () => void {
  if (typeof handler === 'function') unauthorizedHandlers.add(handler);
  return () => {
    unauthorizedHandlers.delete(handler);
  };
}

function emitUnauthorized(): void {
  unauthorizedHandlers.forEach((h) => {
    try {
      h();
    } catch {
      // 单个处理器异常不影响其他
    }
  });
}

async function request<T>(
  url: string,
  method: RequestMethod,
  opts: RequestOptions = {}
): Promise<T> {
  const { basePath } = getConfig();
  // 同源 BFF：业务请求改走宿主同源 /hub/api/*（BFF 服务端推导 hotelId/tenant/brand + 持密钥），
  // 不再跨源直连 whapi（消除 SDK 集成方域名 CORS 白名单）。
  // basePath 为浏览器相对路径，需 location.origin 作 base 构造 URL。
  const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost';
  const fullUrl = new URL(`${basePath}${url}`, origin);

  // 1. params 入 query(先放调用方参数)
  if (opts.params) {
    for (const [k, v] of Object.entries(opts.params)) {
      if (v != null) fullUrl.searchParams.set(k, String(v));
    }
  }

  // dev/test 透传 ?domain= 给 BFF（proxy 据此注入 x-hotel-domain；生产走子域名用 host 头）。
  // 对齐 customer-service.ts / coupons.ts 的 resolveBffDomain；与 resolveHotel 的 getDomain() 一致。
  if (!fullUrl.searchParams.has('domain')) {
    const domain = getDomain();
    if (domain) fullUrl.searchParams.set('domain', domain);
  }

  // 2. 请求体（hotelId/租户/品牌由 BFF 服务端推导，SDK 不注入；Authorization 由 buildContextHeaders 带）
  const body = opts.body;

  // 3. 上下文头(仅 Authorization；X-Tenant-Id/X-Brand-Id 第 4 步下线，BFF derive)
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...buildContextHeaders(),
  };

  // 4. 发请求(带超时)
  // 请求头日志:仅 debug 开启时打印(排查接口用;生产默认关,避免每个请求含埋点都刷日志)
  if (getConfig().debug) {
    console.log('[SDK request] headers', { method, url: fullUrl.toString(), headers, body });
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeout ?? DEFAULT_TIMEOUT);
  let res: Response;
  try {
    res = await fetch(fullUrl.toString(), {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch {
    clearTimeout(timer);
    throw new SdkError(-1, '网络异常,请检查网络连接');
  }
  clearTimeout(timer);

  // 5. 响应处理
  if (res.status === 401) {
    emitUnauthorized();
    throw new SdkError(401, '未登录或登录已过期');
  }
  if (!res.ok) {
    let message = `请求失败 (${res.status})`;
    try {
      const data = await res.json();
      message = data?.message || data?.error?.message || data?.error || message;
    } catch {
      // 非 JSON 响应,保留默认 message
    }
    throw new SdkError(res.status, message);
  }
  return (await res.json()) as T;
}

/** GET */
export function get<T>(
  url: string,
  params?: Record<string, unknown>,
  opts?: RequestOptions
): Promise<T> {
  return request<T>(url, 'GET', { ...opts, params });
}

/** POST */
export function post<T>(url: string, body?: unknown, opts?: RequestOptions): Promise<T> {
  return request<T>(url, 'POST', { ...opts, body });
}

/** PUT */
export function put<T>(url: string, body?: unknown, opts?: RequestOptions): Promise<T> {
  return request<T>(url, 'PUT', { ...opts, body });
}

/** PATCH */
export function patch<T>(url: string, body?: unknown, opts?: RequestOptions): Promise<T> {
  return request<T>(url, 'PATCH', { ...opts, body });
}

/** DELETE */
export function del<T>(
  url: string,
  params?: Record<string, unknown>,
  opts?: RequestOptions
): Promise<T> {
  return request<T>(url, 'DELETE', { ...opts, params });
}
